import React, { useState, useEffect, useRef } from 'react';
import { authHeaders, getApiBaseUrl } from '../utils/auth';

interface GenerationModel {
  generation_mode?: string;
  load_file?: any;
  task_type?: string;
  instruction_display_gen?: string;
  simple_sample_created?: boolean;
  lyrics_before_instrumental?: string;
  previous_generation_mode?: string;
  audio_cover_strength?: number;
  remix_help_group?: any;
  no_fsq?: boolean;
  cover_noise_strength?: number;
  custom_mode_group?: any;
  reference_audio?: string | null;
  captions?: string;
  lyrics?: string;
  instrumental_checkbox?: boolean;
  repainting_start?: number;
  repainting_end?: number;
  repaint_mode?: string;
  repaint_strength?: number;
  repaint_strength_memory?: number;
  bpm?: number | null;
  key_scale?: string;
  time_signature?: string;
  vocal_language?: string;
  bpm_auto?: boolean;
  key_auto?: boolean;
  timesig_auto?: boolean;
  vocal_lang_auto?: boolean;
  audio_duration?: number;
  batch_size_input?: number;
  duration_auto?: boolean;
  simple_query_input?: string;
  simple_vocal_language?: string;
  simple_instrumental_checkbox?: boolean;
  src_audio?: string | null;
  track_name?: string;
  complete_track_classes?: string[];
  lm_codes_audio_upload?: string | null;
  text2music_audio_code_string?: string;
  think_checkbox?: boolean;
  auto_score?: boolean;
  autogen_checkbox?: boolean;
  auto_lrc?: boolean;
  retake_enabled?: boolean;
  retake_variance?: number;
  retake_seed?: string;
  flow_edit_morph?: boolean;
  flow_edit_source_caption?: string;
  flow_edit_source_lyrics?: string;
  flow_edit_n_min?: number;
  flow_edit_n_max?: number;
  flow_edit_n_avg?: number;
  lora_path?: string;
  use_lora_checkbox?: boolean;
  lora_scale_slider?: number;
  lm_temperature?: number;
  lm_cfg_scale?: number;
  lm_top_k?: number;
  lm_top_p?: number;
  lm_negative_prompt?: string;
  use_cot_metas?: boolean;
  use_cot_language?: boolean;
  constrained_decoding_debug?: boolean;
  allow_lm_batch?: boolean;
  use_cot_caption?: boolean;
  lm_use_legacy_cfg_prompt?: boolean;
  inference_steps?: number;
  guidance_scale?: number;
  infer_method?: string;
  sampler_mode?: string;
  velocity_norm_threshold?: number;
  velocity_ema_factor?: number;
  dcw_enabled?: boolean;
  dcw_mode?: string;
  dcw_scaler?: number;
  dcw_high_scaler?: number;
  dcw_wavelet?: string;
  use_adg?: boolean;
  shift?: number;
  custom_timesteps?: string;
  cfg_interval_start?: number;
  cfg_interval_end?: number;
  seed?: string;
  random_seed_checkbox?: boolean;
  mlx_vae_chunk_size?: number;
  audio_format?: string;
  mp3_bitrate?: string;
  mp3_sample_rate?: number;
  score_scale?: number;
  enable_normalization?: boolean;
  normalization_db?: number;
  fade_in_duration?: number;
  fade_out_duration?: number;
  latent_shift?: number;
  latent_rescale?: number;
  lm_batch_chunk_size?: number;
}

const DEFAULT_GEN_STATE: GenerationModel = {
  generation_mode: 'Custom',
  task_type: 'text2music',
  audio_cover_strength: 1.0,
  no_fsq: false,
  cover_noise_strength: 0.0,
  captions: '',
  lyrics: '',
  instrumental_checkbox: false,
  repainting_start: 0.0,
  repainting_end: -1.0,
  repaint_mode: 'balanced',
  repaint_strength: 0.5,
  repaint_strength_memory: 0.5,
  bpm: null,
  key_scale: '',
  time_signature: '',
  vocal_language: 'unknown',
  bpm_auto: true,
  key_auto: true,
  timesig_auto: true,
  vocal_lang_auto: true,
  audio_duration: -1,
  batch_size_input: 1,
  duration_auto: true,
  simple_query_input: '',
  simple_vocal_language: 'unknown',
  simple_instrumental_checkbox: false,
  track_name: '',
  complete_track_classes: [],
  text2music_audio_code_string: '',
  think_checkbox: false,
  auto_score: false,
  autogen_checkbox: false,
  auto_lrc: false,
  retake_enabled: false,
  retake_variance: 0.0,
  retake_seed: '',
  flow_edit_morph: false,
  flow_edit_source_caption: '',
  flow_edit_source_lyrics: '',
  flow_edit_n_min: 0.0,
  flow_edit_n_max: 1.0,
  flow_edit_n_avg: 1,
  lora_path: '',
  use_lora_checkbox: false,
  lora_scale_slider: 1.0,
  lm_temperature: 0.85,
  lm_cfg_scale: 2.0,
  lm_top_k: 0,
  lm_top_p: 0.9,
  lm_negative_prompt: 'NO USER INPUT',
  use_cot_metas: true,
  use_cot_language: true,
  constrained_decoding_debug: false,
  allow_lm_batch: true,
  use_cot_caption: false,
  lm_use_legacy_cfg_prompt: false,
  inference_steps: 8,
  guidance_scale: 7.0,
  infer_method: 'ode',
  sampler_mode: 'euler',
  velocity_norm_threshold: 0.0,
  velocity_ema_factor: 0.0,
  dcw_enabled: false,
  dcw_mode: 'high',
  dcw_scaler: 0.05,
  dcw_high_scaler: 0.05,
  dcw_wavelet: 'haar',
  use_adg: false,
  shift: 1.0,
  custom_timesteps: '',
  cfg_interval_start: 0.0,
  cfg_interval_end: 1.0,
  seed: '-1',
  random_seed_checkbox: true,
  mlx_vae_chunk_size: 1024,
  audio_format: 'mp3',
  mp3_bitrate: '192k',
  mp3_sample_rate: 48000,
  score_scale: 0.5,
  enable_normalization: true,
  normalization_db: -1.0,
  fade_in_duration: 0.0,
  fade_out_duration: 0.0,
  latent_shift: 0.0,
  latent_rescale: 1.0,
  lm_batch_chunk_size: 8,
};

const mediaSrc = (url?: string | null) => {
  if (!url) return '';
  return url.startsWith('http://') || url.startsWith('https://') ? url : `${getApiBaseUrl()}${url}`;
};

const galleryUrl = (item: any) => item?.proxy_url || item?.url || item?.audio_url || '';

const galleryTitle = (item: any) =>
  item?.filename ||
  item?.title ||
  item?.meta?.prompt ||
  item?.metadata?.metadata?.prompt ||
  'AceStep track';

const galleryPrompt = (item: any) =>
  item?.meta?.prompt ||
  item?.metadata?.metadata?.prompt ||
  item?.metadata?.metadata?.lyrics ||
  item?.title ||
  'No prompt info';

const gallerySizeMb = (item: any) => {
  const size = Number(item?.size);
  return Number.isFinite(size) && size > 0 ? `${(size / 1024 / 1024).toFixed(1)} MB` : item?.format || item?.meta?.format || 'audio';
};

const clamp = (value: number | undefined, min: number, max: number, fallback: number) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, n));
};

const taskTypeFromMode = (mode?: string) => {
  if (mode === 'Repaint' || mode === 'Extract' || mode === 'Complete') return 'repaint';
  if (mode === 'Remix') return 'cover';
  return 'text2music';
};

const buildGenerationRequest = (state: GenerationModel) => {
  const taskType = taskTypeFromMode(state.generation_mode);
  const instrumental = Boolean(
    state.generation_mode === 'Simple'
      ? state.simple_instrumental_checkbox
      : state.instrumental_checkbox
  );
  const prompt = (
    state.generation_mode === 'Simple'
      ? state.simple_query_input
      : state.captions
  )?.trim() || '';
  const vocalLanguage = (
    state.generation_mode === 'Simple'
      ? state.simple_vocal_language
      : state.vocal_language
  );
  const audioFormat = state.audio_format === 'wav32' ? 'wav' : state.audio_format || 'mp3';

  const request: Record<string, unknown> = {
    prompt,
    lyrics: instrumental ? '[instrumental]' : state.lyrics || '',
    task_type: taskType,
    audio_duration: state.duration_auto ? -1 : state.audio_duration ?? -1,
    inference_steps: clamp(state.inference_steps, 1, 20, 8),
    guidance_scale: clamp(state.guidance_scale, 1, 15, 7),
    seed: state.random_seed_checkbox ? -1 : Number.parseInt(state.seed || '-1', 10),
    batch_size: clamp(state.batch_size_input, 1, 8, 1),
    audio_format: audioFormat,
    thinking: Boolean(state.think_checkbox),
    use_cot_caption: Boolean(state.use_cot_caption),
    enhance_lyrics: false,
    lm_temperature: clamp(state.lm_temperature, 0, 2, 0.85),
    lm_cfg_scale: clamp(state.lm_cfg_scale, 1, 3, 2),
    vocal_language: vocalLanguage && vocalLanguage !== 'unknown' ? vocalLanguage : 'en',
    instrumental,
    bpm: state.bpm_auto || state.bpm == null ? 'auto' : String(state.bpm),
    key_scale: state.key_auto ? '' : state.key_scale || '',
    time_signature: state.timesig_auto ? '' : state.time_signature || '',
    track_name: state.track_name || 'vocals',
    audio_cover_strength: clamp(state.audio_cover_strength, 0, 1, 1),
    cover_noise_strength: clamp(state.cover_noise_strength, 0, 1, 0),
    repainting_start: state.repainting_start ?? 0,
    repainting_end: state.repainting_end ?? -1,
    repaint_mode: state.repaint_mode || 'balanced',
    repaint_strength: clamp(state.repaint_strength, 0, 1, 0.5),
    flow_edit_source_caption: state.flow_edit_source_caption || '',
    flow_edit_source_lyrics: state.flow_edit_source_lyrics || '',
    flow_edit_n_min: state.flow_edit_n_min ?? 0,
    flow_edit_n_max: state.flow_edit_n_max ?? 1,
    flow_edit_n_avg: state.flow_edit_n_avg ?? 1,
    auto_score: Boolean(state.auto_score),
    auto_lrc: Boolean(state.auto_lrc),
    autogen: Boolean(state.autogen_checkbox),
  };

  if (state.reference_audio && (taskType === 'text2music' || taskType === 'cover')) {
    request.reference_audio_base64 = state.reference_audio;
  }
  if (state.src_audio && taskType === 'repaint') {
    request.src_audio_base64 = state.src_audio;
  }

  return request;
};

const AceStepPanel: React.FC = () => {
  const [activeSubTab, setActiveSubTab] = useState<'generation' | 'lora'>('generation');
  const [genState, setGenState] = useState<GenerationModel>(DEFAULT_GEN_STATE);
  const [isGenerating, setIsGenerating] = useState(false);
  const [galleryItems, setGalleryItems] = useState<any[]>([]);
  const [statusText, setStatusText] = useState('AceStep 1.5 System Ready');
  const audioRef = useRef<HTMLAudioElement>(null);
  
  // File inputs
  const referenceAudioInput = useRef<HTMLInputElement>(null);
  const srcAudioInput = useRef<HTMLInputElement>(null);
  const lmCodesUploadInput = useRef<HTMLInputElement>(null);

  const fetchGallery = async () => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/v1/acestep/gallery`, { headers: authHeaders() });
      if (response.ok) {
        const data = await response.json();
        setGalleryItems(data.items || data.files || []);
      }
    } catch (e) {
      console.error('Gallery error', e);
    }
  };

  useEffect(() => {
    fetchGallery();
    const iv = setInterval(fetchGallery, 5000);
    return () => clearInterval(iv);
  }, []);

  const handleChange = (k: keyof GenerationModel, v: any) => {
    setGenState(prev => ({ ...prev, [k]: v }));
  };

  const toBase64 = (file: File): Promise<string> => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = error => reject(error);
  });

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>, key: keyof GenerationModel) => {
    if (e.target.files && e.target.files[0]) {
      try {
        const b64 = await toBase64(e.target.files[0]);
        handleChange(key, b64);
        setStatusText(`File loaded for ${key}`);
      } catch (err) {
        setStatusText(`Error loading file: ${err}`);
      }
    }
  };

  const handleGenerate = async () => {
    setIsGenerating(true);
    setStatusText('Processing request...');
    
    const requestPayload = buildGenerationRequest(genState);

    try {
      const response = await fetch(`${getApiBaseUrl()}/v1/acestep/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders(),
        },
        body: JSON.stringify(requestPayload)
      });
      
      const result = await response.json();
      if (result.status === "success") {
        setStatusText('Generation complete!');
        fetchGallery();
      } else {
        setStatusText(`Generation failed: ${result.message}`);
      }
    } catch (e: any) {
      setStatusText(`Request error: ${e.message}`);
    } finally {
      setIsGenerating(false);
    }
  };

  const sendToRemix = async (item: any) => {
    try {
       // Download the item and convert to base64
       const url = mediaSrc(galleryUrl(item));
       if (!url) throw new Error('Gallery item has no playable URL');
       const resp = await fetch(url);
       const blob = await resp.blob();
       const file = new File([blob], galleryTitle(item), { type: blob.type });
       const b64 = await toBase64(file);
       
       setGenState(prev => ({
         ...prev,
         generation_mode: 'Custom', // switch to a mode that supports reference
         reference_audio: b64,
         captions: galleryPrompt(item) || prev.captions
       }));
       setStatusText(`Loaded ${galleryTitle(item)} into Reference Audio for Remix.`);
       setActiveSubTab('generation');
    } catch (e) {
       setStatusText(`Failed to load for Remix: ${e}`);
    }
  };

  const sendToRepaint = async (item: any) => {
    try {
       const url = mediaSrc(galleryUrl(item));
       if (!url) throw new Error('Gallery item has no playable URL');
       const resp = await fetch(url);
       const blob = await resp.blob();
       const file = new File([blob], galleryTitle(item), { type: blob.type });
       const b64 = await toBase64(file);
       
       setGenState(prev => ({
         ...prev,
         generation_mode: 'Repaint',
         src_audio: b64,
         captions: galleryPrompt(item) || prev.captions
       }));
       setStatusText(`Loaded ${galleryTitle(item)} into Source Audio for Repaint.`);
       setActiveSubTab('generation');
    } catch (e) {
       setStatusText(`Failed to load for Repaint: ${e}`);
    }
  };

  const renderBasicControls = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
      <div className="bg-[#1e222d] border border-[#2a2e39] p-4 rounded shadow-lg col-span-full">
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs font-bold text-slate-400 mb-1 uppercase tracking-wider">Mode</label>
            <select
              title="Mode"
              className="w-full bg-[#131722] text-sm text-slate-200 border border-[#2a2e39] rounded px-3 py-2"
              value={genState.generation_mode}
              onChange={e => handleChange('generation_mode', e.target.value)}
            >
              <option value="Simple">Simple</option>
              <option value="Custom">Custom</option>
              <option value="Remix">Remix</option>
              <option value="Repaint">Repaint</option>
              <option value="Extract">Extract</option>
              <option value="Complete">Complete</option>
            </select>
          </div>
          {genState.generation_mode !== 'Simple' && (
            <div className="flex items-center gap-4 mt-4">
              <label className="flex items-center gap-2 text-sm text-slate-300">
                <input type="checkbox" checked={genState.think_checkbox} onChange={e => handleChange('think_checkbox', e.target.checked)} className="rounded border-slate-600 bg-[#131722] text-purple-500 focus:ring-purple-500" />
                Think (CoT)
              </label>
              <label className="flex items-center gap-2 text-sm text-slate-300">
                <input type="checkbox" checked={genState.auto_score} onChange={e => handleChange('auto_score', e.target.checked)} className="rounded border-slate-600 bg-[#131722] text-purple-500 focus:ring-purple-500" />
                Auto Score
              </label>
              <label className="flex items-center gap-2 text-sm text-slate-300">
                <input type="checkbox" checked={genState.autogen_checkbox} onChange={e => handleChange('autogen_checkbox', e.target.checked)} className="rounded border-slate-600 bg-[#131722] text-purple-500 focus:ring-purple-500" />
                Auto Gen
              </label>
              <label className="flex items-center gap-2 text-sm text-slate-300">
                <input type="checkbox" checked={genState.auto_lrc} onChange={e => handleChange('auto_lrc', e.target.checked)} className="rounded border-slate-600 bg-[#131722] text-purple-500 focus:ring-purple-500" />
                Auto LRC
              </label>
            </div>
          )}
        </div>
      </div>
      
      {/* Prompts Area */}
      <div className="bg-[#1e222d] border border-[#2a2e39] p-4 rounded shadow-lg col-span-full xl:col-span-2 space-y-4">
        {genState.generation_mode === 'Simple' ? (
          <div>
             <label className="block text-xs font-bold text-slate-400 mb-1 uppercase tracking-wider">Simple Query</label>
             <textarea
               className="w-full bg-[#131722] text-sm text-slate-200 border border-[#2a2e39] rounded px-3 py-2 min-h-[80px]"
               value={genState.simple_query_input}
               onChange={e => handleChange('simple_query_input', e.target.value)}
             />
             <div className="flex gap-4 mt-2">
                <select title="Language" className="bg-[#131722] text-sm text-slate-200 border border-[#2a2e39] rounded px-2 py-1" value={genState.simple_vocal_language} onChange={e => handleChange('simple_vocal_language', e.target.value)}>
                   <option value="unknown">Instrumental / auto</option>
                   <option value="en">English</option>
                   <option value="ja">Japanese</option>
                   <option value="zh">Chinese</option>
                </select>
                <label className="flex items-center gap-2 text-sm text-slate-300">
                  <input type="checkbox" checked={genState.simple_instrumental_checkbox} onChange={e => handleChange('simple_instrumental_checkbox', e.target.checked)} className="rounded border-slate-600 bg-[#131722]" />
                  Instrumental
                </label>
             </div>
          </div>
        ) : (
          <div className="space-y-4">
             <div>
               <label className="block text-xs font-bold text-slate-400 mb-1 uppercase tracking-wider">Captions</label>
               <textarea
                 className="w-full bg-[#131722] text-sm text-slate-200 border border-[#2a2e39] rounded px-3 py-2 min-h-[100px]"
                 value={genState.captions}
                 onChange={e => handleChange('captions', e.target.value)}
                 placeholder="Describe the track..."
               />
             </div>
             <div>
               <label className="block text-xs font-bold text-slate-400 mb-1 uppercase tracking-wider">Lyrics</label>
               <textarea
                 className="w-full bg-[#131722] text-sm text-slate-200 border border-[#2a2e39] rounded px-3 py-2 min-h-[100px]"
                 value={genState.lyrics}
                 onChange={e => handleChange('lyrics', e.target.value)}
                 placeholder="[Verse 1]..."
               />
               <label className="flex items-center gap-2 text-sm text-slate-300 mt-2">
                 <input type="checkbox" checked={genState.instrumental_checkbox} onChange={e => handleChange('instrumental_checkbox', e.target.checked)} className="rounded border-slate-600 bg-[#131722]" />
                 Instrumental Only
               </label>
             </div>
          </div>
        )}
      </div>

      {/* Audio Inputs Area */}
      {genState.generation_mode !== 'Simple' && (
      <div className="bg-[#1e222d] border border-[#2a2e39] p-4 rounded shadow-lg col-span-full xl:col-span-1 space-y-4">
        {['Custom', 'Remix'].includes(genState.generation_mode || '') && (
          <div>
            <label className="block text-xs font-bold text-slate-400 mb-1 uppercase tracking-wider">Reference Audio</label>
            <input type="file" ref={referenceAudioInput} onChange={e => handleFileUpload(e, 'reference_audio')} accept="audio/*" className="hidden" />
            <button onClick={() => referenceAudioInput.current?.click()} className="w-full py-2 px-4 bg-[#2a2e39] text-slate-300 text-sm font-semibold rounded hover:bg-[#343a46] border border-[#3b414f]">
              {genState.reference_audio ? 'Audio Loaded (Click to replace)' : 'Upload Audio'}
            </button>
            {genState.reference_audio && (
              <audio src={genState.reference_audio} controls className="w-full mt-2 h-8" />
            )}
          </div>
        )}

        {['Repaint', 'Extract', 'Complete'].includes(genState.generation_mode || '') && (
          <div>
            <label className="block text-xs font-bold text-slate-400 mb-1 uppercase tracking-wider">Source Audio</label>
            <input type="file" ref={srcAudioInput} onChange={e => handleFileUpload(e, 'src_audio')} accept="audio/*" className="hidden" />
            <button onClick={() => srcAudioInput.current?.click()} className="w-full py-2 px-4 bg-[#2a2e39] text-slate-300 text-sm font-semibold rounded hover:bg-[#343a46] border border-[#3b414f]">
              {genState.src_audio ? 'Audio Loaded (Click to replace)' : 'Upload Audio'}
            </button>
            {genState.src_audio && (
              <audio src={genState.src_audio} controls className="w-full mt-2 h-8" />
            )}
          </div>
        )}

        {genState.generation_mode === 'Repaint' && (
          <div className="pt-4 border-t border-[#2a2e39] space-y-3">
             <label className="block text-xs font-bold text-slate-400 mb-1 uppercase tracking-wider">Repaint Controls</label>
             <div className="flex gap-2">
               <div className="flex-1">
                 <label className="text-[10px] text-slate-500">Start (s)</label>
                 <input type="number" title="start" className="w-full bg-[#131722] text-sm text-slate-200 border border-[#2a2e39] rounded px-2" value={genState.repainting_start} onChange={e => handleChange('repainting_start', parseFloat(e.target.value))} />
               </div>
               <div className="flex-1">
                 <label className="text-[10px] text-slate-500">End (s)</label>
                 <input type="number" title="end" className="w-full bg-[#131722] text-sm text-slate-200 border border-[#2a2e39] rounded px-2" value={genState.repainting_end} onChange={e => handleChange('repainting_end', parseFloat(e.target.value))} />
               </div>
             </div>
             <div>
               <label className="text-[10px] text-slate-500">Mode</label>
               <select title="mode" className="w-full bg-[#131722] text-sm text-slate-200 border border-[#2a2e39] rounded px-2 py-1" value={genState.repaint_mode} onChange={e => handleChange('repaint_mode', e.target.value)}>
                 <option value="conservative">Conservative</option>
                 <option value="balanced">Balanced</option>
                 <option value="aggressive">Aggressive</option>
               </select>
             </div>
             <div>
                <label className="text-[10px] text-slate-500 flex justify-between">Strength <span>{genState.repaint_strength}</span></label>
                <input type="range" title="strength" min="0" max="1" step="0.05" className="w-full" value={genState.repaint_strength} onChange={e => handleChange('repaint_strength', parseFloat(e.target.value))} />
             </div>
          </div>
        )}

        {/* Audio Cover Strength for Remix/Custom */}
        {['Custom'].includes(genState.generation_mode || '') && (
          <div className="pt-4 border-t border-[#2a2e39]">
            <label className="block text-xs font-bold text-slate-400 mb-1 uppercase tracking-wider flex justify-between">Audio Cover Strength <span>{genState.audio_cover_strength}</span></label>
            <input type="range" title="Audio Cover Strength" min="0" max="1" step="0.01" className="w-full" value={genState.audio_cover_strength} onChange={e => handleChange('audio_cover_strength', parseFloat(e.target.value))} />
          </div>
        )}

      </div>
      )}
    </div>
  );

  const renderAdvancedSettings = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
      {/* Sampling Parameters */}
      <div className="bg-[#1e222d] border border-[#2a2e39] p-4 rounded shadow-lg space-y-3">
        <h3 className="text-sm font-bold text-slate-300 uppercase underline decoration-purple-500 mb-2">LM Settings</h3>
        <div>
          <label className="text-[10px] text-slate-500 flex justify-between">Temperature <span>{genState.lm_temperature}</span></label>
          <input type="range" title="t" min="0" max="2" step="0.1" className="w-full accent-purple-500" value={genState.lm_temperature} onChange={e => handleChange('lm_temperature', parseFloat(e.target.value))} />
        </div>
        <div>
          <label className="text-[10px] text-slate-500 flex justify-between">CFG Scale <span>{genState.lm_cfg_scale}</span></label>
          <input type="range" title="cfg" min="1" max="3" step="0.1" className="w-full accent-purple-500" value={genState.lm_cfg_scale} onChange={e => handleChange('lm_cfg_scale', parseFloat(e.target.value))} />
        </div>
        <div className="flex gap-2">
            <div className="flex-1">
                <label className="text-[10px] text-slate-500">Top-P</label>
                <input type="number" title="top p" className="w-full bg-[#131722] text-sm text-slate-200 border border-[#2a2e39] rounded px-2 py-1" value={genState.lm_top_p} onChange={e => handleChange('lm_top_p', parseFloat(e.target.value))} />
            </div>
            <div className="flex-1">
                <label className="text-[10px] text-slate-500">Top-K</label>
                <input type="number" title="top k" className="w-full bg-[#131722] text-sm text-slate-200 border border-[#2a2e39] rounded px-2 py-1" value={genState.lm_top_k} onChange={e => handleChange('lm_top_k', parseInt(e.target.value))} />
            </div>
        </div>
        <div>
            <label className="text-[10px] text-slate-500">Negative Prompt</label>
            <input type="text" title="negative" className="w-full bg-[#131722] text-sm text-slate-200 border border-[#2a2e39] rounded px-2 py-1" value={genState.lm_negative_prompt} onChange={e => handleChange('lm_negative_prompt', e.target.value)} />
        </div>
      </div>

      <div className="bg-[#1e222d] border border-[#2a2e39] p-4 rounded shadow-lg space-y-3">
        <h3 className="text-sm font-bold text-slate-300 uppercase underline decoration-[#10b981] mb-2">Generator Settings</h3>
        <div>
          <label className="text-[10px] text-slate-500 flex justify-between">Guidance Scale <span>{genState.guidance_scale}</span></label>
          <input type="range" title="guidance" min="1" max="15" step="0.1" className="w-full accent-[#10b981]" value={genState.guidance_scale} onChange={e => handleChange('guidance_scale', parseFloat(e.target.value))} />
        </div>
        <div>
          <label className="text-[10px] text-slate-500 flex justify-between">Inference Steps <span>{genState.inference_steps}</span></label>
          <input type="range" title="steps" min="1" max="20" step="1" className="w-full accent-[#10b981]" value={genState.inference_steps} onChange={e => handleChange('inference_steps', parseInt(e.target.value))} />
        </div>
        <div className="flex gap-2">
            <div className="flex-1">
                <label className="text-[10px] text-slate-500">Method</label>
                <select title="method" className="w-full bg-[#131722] text-sm text-slate-200 border border-[#2a2e39] rounded px-2 py-1" value={genState.infer_method} onChange={e => handleChange('infer_method', e.target.value)}>
                    <option value="ode">ODE</option>
                    <option value="sde">SDE</option>
                </select>
            </div>
            <div className="flex-1">
                <label className="text-[10px] text-slate-500">Sampler</label>
                <select title="sampler" className="w-full bg-[#131722] text-sm text-slate-200 border border-[#2a2e39] rounded px-2 py-1" value={genState.sampler_mode} onChange={e => handleChange('sampler_mode', e.target.value)}>
                    <option value="euler">Euler</option>
                    <option value="heun">Heun</option>
                </select>
            </div>
        </div>
      </div>

      <div className="bg-[#1e222d] border border-[#2a2e39] p-4 rounded shadow-lg space-y-3">
        <h3 className="text-sm font-bold text-slate-300 uppercase underline decoration-blue-500 mb-2">Output Format</h3>
        <div className="flex gap-2">
            <div className="flex-1">
                <label className="text-[10px] text-slate-500">Format</label>
                <select title="format" className="w-full bg-[#131722] text-sm text-slate-200 border border-[#2a2e39] rounded px-2 py-1" value={genState.audio_format} onChange={e => handleChange('audio_format', e.target.value)}>
                    <option value="mp3">MP3</option>
                    <option value="wav">WAV (16-bit)</option>
                    <option value="wav32">WAV (32-bit)</option>
                </select>
            </div>
            <div className="flex-1">
                <label className="text-[10px] text-slate-500">Bitrate</label>
                <select title="bitrate" className="w-full bg-[#131722] text-sm text-slate-200 border border-[#2a2e39] rounded px-2 py-1" value={genState.mp3_bitrate} onChange={e => handleChange('mp3_bitrate', e.target.value)}>
                    <option value="128k">128k</option>
                    <option value="192k">192k</option>
                    <option value="320k">320k</option>
                </select>
            </div>
        </div>
        <div className="pt-2 border-t border-[#2a2e39]">
            <label className="flex items-center gap-2 text-sm text-slate-300 mb-2">
                <input type="checkbox" checked={genState.enable_normalization} onChange={e => handleChange('enable_normalization', e.target.checked)} className="rounded border-slate-600 bg-[#131722]" />
                Normalize
            </label>
            <label className="text-[10px] text-slate-500 flex justify-between">Norm DB <span>{genState.normalization_db}</span></label>
            <input type="range" title="db" min="-10" max="0" step="0.1" className="w-full accent-blue-500" value={genState.normalization_db} onChange={e => handleChange('normalization_db', parseFloat(e.target.value))} />
        </div>
      </div>

      <div className="bg-[#1e222d] border border-[#2a2e39] p-4 rounded shadow-lg space-y-3">
        <h3 className="text-sm font-bold text-slate-300 uppercase underline decoration-yellow-500 mb-2">Toggles & Variables</h3>
        <label className="flex justify-between items-center text-sm text-slate-300">
            Batch Size
            <input type="number" title="batch" className="w-16 bg-[#131722] text-sm text-slate-200 border border-[#2a2e39] rounded px-2 py-1 text-center" value={genState.batch_size_input} onChange={e => handleChange('batch_size_input', parseInt(e.target.value))} min={1} max={8} />
        </label>
        <label className="flex items-center gap-2 text-sm text-slate-300">
            <input type="checkbox" checked={genState.retake_enabled} onChange={e => handleChange('retake_enabled', e.target.checked)} className="rounded border-slate-600 bg-[#131722]" />
            Retake Enabled
        </label>
        {genState.retake_enabled && (
             <div>
                <label className="text-[10px] text-slate-500 flex justify-between">Variance <span>{genState.retake_variance}</span></label>
                <input type="range" title="variance" min="0" max="1" step="0.01" className="w-full accent-yellow-500" value={genState.retake_variance} onChange={e => handleChange('retake_variance', parseFloat(e.target.value))} />
             </div>
        )}
        <hr className="border-[#2a2e39]" />
        <label className="flex items-center gap-2 text-sm text-slate-300">
            <input type="checkbox" checked={genState.use_lora_checkbox} onChange={e => handleChange('use_lora_checkbox', e.target.checked)} className="rounded border-slate-600 bg-[#131722]" />
            Enable LoRA
        </label>
        {genState.use_lora_checkbox && (
             <div className="space-y-2">
                <input type="text" title="LORA path" placeholder="LoRA model path..." className="w-full bg-[#131722] text-xs text-slate-200 border border-[#2a2e39] rounded px-2 py-1" value={genState.lora_path} onChange={e => handleChange('lora_path', e.target.value)} />
                <label className="text-[10px] text-slate-500 flex justify-between">Scale <span>{genState.lora_scale_slider}</span></label>
                <input type="range" title="LORA scale" min="0" max="1" step="0.05" className="w-full accent-yellow-500" value={genState.lora_scale_slider} onChange={e => handleChange('lora_scale_slider', parseFloat(e.target.value))} />
             </div>
        )}
      </div>
    </div>
  );

  const renderLoRATraining = () => (
      <div className="text-center py-20 text-slate-500">
         <i className="fas fa-hammer text-4xl mb-4"></i>
         <h2 className="text-xl font-bold">LoRA Training</h2>
         <p className="mt-2 text-sm max-w-md mx-auto">This section provides tools for Dataset Generation, Preprocessing and LORA fine-tuning. Configuration will be fully surfaced in subsequent updates.</p>
      </div>
  );

  const renderGallery = () => (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-2 px-2 border-b border-[#2a2e39] pb-2">
          <h2 className="text-md font-bold text-white uppercase tracking-wider">Storage Output</h2>
          <span className="text-xs text-slate-400">{galleryItems.length} tracks found</span>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {galleryItems.map((item, idx) => {
            const url = mediaSrc(galleryUrl(item));
            const title = galleryTitle(item);
            const prompt = galleryPrompt(item);
            return (
              <div key={idx} className="bg-[#1e222d] border border-[#2a2e39] rounded-lg p-3 hover:border-purple-500/50 transition-colors relative flex flex-col justify-between">
                 <div>
                    <div className="flex justify-between items-start mb-2">
                        <h4 className="text-sm font-bold text-slate-200 truncate pr-2 flex-grow" title={title}>{title}</h4>
                        <span className="shrink-0 text-[10px] font-mono bg-[#2a2e39] text-slate-400 px-1.5 py-0.5 rounded">
                            {gallerySizeMb(item)}
                        </span>
                    </div>
                    <div className="text-xs text-slate-400 mb-3 space-y-1 bg-[#131722] p-2 rounded max-h-16 overflow-y-auto">
                        <strong className="text-purple-400">Prompt:</strong> {prompt} 
                    </div>
                 </div>

                 <div className="space-y-3">
                    {url ? (
                      <audio src={url} controls className="w-full h-8 outline-none" />
                    ) : (
                      <div className="text-[10px] text-red-400 bg-red-500/10 border border-red-500/20 rounded px-2 py-1">
                        Missing audio URL
                      </div>
                    )}
                    
                    <div className="flex gap-2 justify-end">
                       <button onClick={() => sendToRemix(item)} disabled={!url} className="text-[10px] bg-blue-500/20 text-blue-400 hover:bg-blue-500/40 disabled:opacity-40 disabled:cursor-not-allowed px-2 py-1 rounded font-bold uppercase transition-colors">
                          <i className="fas fa-random mr-1"></i> Remx
                       </button>
                       <button onClick={() => sendToRepaint(item)} disabled={!url} className="text-[10px] bg-orange-500/20 text-orange-400 hover:bg-orange-500/40 disabled:opacity-40 disabled:cursor-not-allowed px-2 py-1 rounded font-bold uppercase transition-colors">
                          <i className="fas fa-paint-brush mr-1"></i> Rpt
                       </button>
                       <a href={url || undefined} download={title} className={`text-[10px] bg-green-500/20 text-green-400 hover:bg-green-500/40 px-2 py-1 rounded font-bold uppercase transition-colors ${url ? '' : 'pointer-events-none opacity-40'}`}>
                          <i className="fas fa-download"></i>
                       </a>
                    </div>
                 </div>
              </div>
            );
        })}
        {galleryItems.length === 0 && (
            <div className="col-span-full py-10 text-center text-slate-500 text-sm">
                No items in gallery.
            </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="flex flex-col h-full bg-[#131722] text-[#d1d4dc] overflow-y-auto p-4 md:p-6 custom-scrollbar">
      
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 border-b border-[#2a2e39] pb-4">
        <div>
           <h1 className="text-2xl font-black text-white tracking-tighter uppercase italic flex items-center gap-3">
             <i className="fas fa-music text-purple-500"></i> Code UI
             <span className="bg-purple-600/20 text-purple-400 text-[10px] px-2 py-0.5 rounded not-italic font-bold tracking-widest border border-purple-500/30">ACE-STEP V1.5</span>
           </h1>
           <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mt-1 ml-9">
              {statusText}
           </p>
        </div>
        
        <div className="flex items-center gap-3 mt-4 md:mt-0">
           <button onClick={() => setActiveSubTab('generation')} className={`text-xs font-bold uppercase tracking-widest px-4 py-2 rounded transition-colors ${activeSubTab === 'generation' ? 'bg-[#2a2e39] text-white border-b-2 border-purple-500' : 'text-slate-500 hover:text-slate-300'} `}>
             Generation
           </button>
           <button onClick={() => setActiveSubTab('lora')} className={`text-xs font-bold uppercase tracking-widest px-4 py-2 rounded transition-colors ${activeSubTab === 'lora' ? 'bg-[#2a2e39] text-white border-b-2 border-purple-500' : 'text-slate-500 hover:text-slate-300'} `}>
             LoRA Training
           </button>
           
           <button 
             onClick={handleGenerate} 
             disabled={isGenerating}
             className={`ml-4 text-xs font-black uppercase tracking-widest px-6 py-2.5 flex items-center gap-2 rounded shadow-lg transition-all ${isGenerating ? 'bg-purple-800 text-white cursor-not-allowed opacity-70' : 'bg-purple-600 hover:bg-purple-500 text-white hover:scale-[1.02]'}`}
           >
              {isGenerating ? <><i className="fas fa-spinner fa-spin"></i> Processing...</> : <><i className="fas fa-play"></i> Generate</>}
           </button>
        </div>
      </div>

      <div className="flex-1 flex flex-col xl:flex-row gap-6">
        <div className="flex-1 w-full xl:w-2/3">
           {activeSubTab === 'generation' ? (
             <div className="space-y-2">
                 {renderBasicControls()}
                 {renderAdvancedSettings()}
             </div>
           ) : (
               renderLoRATraining()
           )}
        </div>
        
        <div className="w-full xl:w-1/3 border-l border-[#2a2e39] pl-0 xl:pl-6">
           {renderGallery()}
        </div>
      </div>
      
    </div>
  );
};

export default AceStepPanel;
